<template>
    <Portal to="modal">
        <ModalContainer
            key="weakestConceptQuizModal"
            :is-dark-mode="isDarkMode"
            @close="emitClose"
        >
            <template #modal>
                <Modal
                    :show-close-button="true"
                    class="weakest-concept-quiz__modal"
                    :is-dark-mode="isDarkMode"
                    @close="emitClose"
                >
                    <div class="weakest-concept-quiz">
                        <Icon
                            v-dark
                            type="concept"
                            class="weakest-concept-quiz__icon"
                        />
                        <div v-dark class="weakest-concept-quiz__title">
                            Concept Quiz
                        </div>
                        <SubjectCard
                            class="weakest-concept-quiz__concept"
                            :subject="{
                                name: weakestConcept.concept,
                                correctCount: weakestConcept.totalCorrectAnswersCount,
                                answeredCount: weakestConcept.totalAnswersCount,
                            }"
                        />
                        <div class="weakest-concept-quiz__buttons">
                            <PocketButton
                                :is-dark-mode="isDarkMode"
                                type="secondary"
                                @click="emitClose"
                            >
                                Cancel
                            </PocketButton>
                            <PocketButton
                                :is-dark-mode="isDarkMode"
                                :is-loading="isLoading"
                                @click="startQuiz"
                            >
                                Start {{ QUESTION_COUNT }} Question Quiz
                            </PocketButton>
                        </div>
                    </div>
                </Modal>
            </template>
        </ModalContainer>
    </Portal>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue'
import { useRouter } from 'vue-router'
import UIKit from '@pocketprep/ui-kit'
import type { Study } from '@pocketprep/types'
import SubjectCard from '@/components/Stats/SubjectCard.vue'
import { quizModule } from '@/store/quiz/module'
import { userModule } from '@/store/user/module'

const {
    Icon,
    Modal,
    ModalContainer,
    Button: PocketButton,
} = UIKit

const QUESTION_COUNT = 10

const props = defineProps<{
    weakestConcept: Study.Cloud.TWeakestConcept
}>()

const emit = defineEmits<{
    close: []
    started: []
}>()

const router = useRouter()

const isLoading = ref(false)

const isDarkMode = computed(() => userModule.state.settings.isDarkMode)

const emitClose = () => {
    emit('close')
}

const startQuiz = async () => {
    if (isLoading.value) {
        return
    }

    isLoading.value = true
    try {
        const conceptQuizId = props.weakestConcept.conceptQuizId
        const questions = await quizModule.actions.fetchConceptQuizQuestions({
            conceptQuizId,
            questionCount: QUESTION_COUNT,
        })
        quizModule.actions.createConceptQuiz({ conceptQuizId, questions })
        emit('started')

        router.push({ name: 'quiz' })
    } finally {
        isLoading.value = false
    }
}
</script>

<style lang="scss" scoped>
.weakest-concept-quiz {
    width: 492px;
    text-align: center;
    padding: 36px 40px 24px;
    box-sizing: border-box;

    @include breakpoint(black-bear) {
        max-width: 326px;
        margin: 0 auto;
        width: 100%;
        padding: 36px 16px 24px;
    }

    &__modal {
        left: 50%;
        position: absolute;
        transform: translateX(-50%);
        top: 80px;

        @include breakpoint(black-bear) {
            width: 356px;
            height: auto;
            top: 50%;
            transform: translate(-50%, -50%);
        }
    }

    &__icon {
        width: 25px;
        height: 24px;
        display: block;
        margin: 0 auto 6px;
        color: $ash;

        &--dark {
            color: $fog;
        }
    }

    &__title {
        font-size: 18px;
        line-height: 25px;
        letter-spacing: 0.3px;
        font-weight: 600;
        color: $brand-black;

        &--dark {
            color: $fog;
        }
    }

    &__concept {
        margin: 40px auto;
        text-align: left;
        width: 348px;

        @include breakpoint(black-bear) {
            width: auto;
        }
    }

    &__buttons {
        display: flex;
        justify-content: center;
        align-items: center;
        gap: 8px;
    }
}
</style>
